import { Component, Input, Output, EventEmitter, forwardRef, HostListener, ElementRef, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-multiselect',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './multiselect.component.html',
  styleUrl: './multiselect.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true
    }
  ]
})
export class MultiselectComponent implements ControlValueAccessor {
  @Input() options: any[] = [];
  @Input() optionLabel: string | undefined = undefined;
  @Input() placeholder: string = 'Select Items';
  @Input() styleClass: string = '';
  @Input() disabled: boolean = false;
  @Input() showClear: boolean = true;
  @Input() display: 'comma' | 'chip' = 'chip';
  @Input() filter: boolean = false;
  @Input() appendTo: string | null = 'body';
  /**
   * Maximum number of items that can be selected.
   * 0 or undefined means "no limit". Use 1 for single-select behavior with checkbox UI.
   */
  @Input() maxSelected: number = 0;

  @Output() onChange = new EventEmitter<any>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onTriggerMousedown = new EventEmitter<MultiselectComponent>();

  onTriggerMousedownHandler(): void {
    this.onTriggerMousedown.emit(this);
  }

  selectedItems: any[] = [];
  isOpen = false;
  searchTerm = '';
  
  private onChangeCallback = (value: any[]) => {};
  private onTouchedCallback = () => {};

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Only close if click is outside the multiselect component
    // The trigger click has stopPropagation, so this won't fire for trigger clicks
    if (!this.elementRef.nativeElement.contains(target)) {
      this.isOpen = false;
    }
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      const wasOpen = this.isOpen;
      this.isOpen = !this.isOpen;
      if (this.isOpen && !wasOpen) {
        this.onTouchedCallback();
        this.onOpen.emit();
      } else if (!this.isOpen && wasOpen) {
        this.onClose.emit();
      }
    }
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.onClose.emit();
    }
  }

  getDisplayValue(): string {
    if (this.selectedItems.length === 0) {
      return this.placeholder;
    }
    
    if (this.display === 'chip') {
      return `${this.selectedItems.length} selected`;
    }
    
    return this.selectedItems.map(item => this.getOptionLabel(item)).join(', ');
  }

  getOptionLabel(option: any): string {
    if (this.optionLabel && typeof option === 'object' && option !== null) {
      return option[this.optionLabel] || String(option);
    }
    return String(option);
  }

  getOptionValue(option: any): any {
    return option;
  }

  isSelected(option: any): boolean {
    return this.selectedItems.some(item => {
      if (this.optionLabel && typeof item === 'object' && typeof option === 'object') {
        return item[this.optionLabel] === option[this.optionLabel];
      }
      return item === option;
    });
  }

  toggleSelection(option: any): void {
    const optionValue = this.getOptionValue(option);
    const index = this.selectedItems.findIndex(item => {
      if (this.optionLabel && typeof item === 'object' && typeof optionValue === 'object') {
        return item[this.optionLabel] === optionValue[this.optionLabel];
      }
      return item === optionValue;
    });

    if (index > -1) {
      // Deselect if already selected
      this.selectedItems.splice(index, 1);
    } else {
      // If maxSelected is 1, behave like single-select: replace previous selection
      if (this.maxSelected === 1) {
        this.selectedItems = [optionValue];
      } else if (this.maxSelected > 1 && this.selectedItems.length >= this.maxSelected) {
        // If there is a limit > 1, remove the oldest selection before adding new
        this.selectedItems.shift();
        this.selectedItems.push(optionValue);
      } else {
        // No limit or under limit
        this.selectedItems.push(optionValue);
      }
    }

    this.onChangeCallback([...this.selectedItems]);
    this.onChange.emit([...this.selectedItems]);
  }

  get filteredOptions(): any[] {
    if (!this.searchTerm) {
      return this.options;
    }
    const term = this.searchTerm.toLowerCase();
    return this.options.filter(option => {
      const label = this.getOptionLabel(option).toLowerCase();
      return label.includes(term);
    });
  }

  clearSelection(): void {
    this.selectedItems = [];
    this.onChangeCallback([]);
    this.onChange.emit([]);
  }

  removeChip(item: any, event: Event): void {
    event.stopPropagation();
    const index = this.selectedItems.findIndex(selected => {
      if (this.optionLabel && typeof selected === 'object' && typeof item === 'object') {
        return selected[this.optionLabel] === item[this.optionLabel];
      }
      return selected === item;
    });
    
    if (index > -1) {
      this.selectedItems.splice(index, 1);
      this.onChangeCallback([...this.selectedItems]);
      this.onChange.emit([...this.selectedItems]);
    }
  }

  trackByOption(index: number, option: any): any {
    if (this.optionLabel && typeof option === 'object' && option !== null) {
      return option[this.optionLabel] || index;
    }
    return option || index;
  }

  getOptionClasses(option: any): string {
    const baseClasses = 'flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors';
    return baseClasses;
  }

  writeValue(value: any[]): void {
    this.selectedItems = value || [];
  }

  registerOnChange(fn: (value: any[]) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
